import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { CriteriaGroup } from '@/types/evaluation.types';
import { generateId } from '@/lib/id';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface CriteriaGroupFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (group: CriteriaGroup) => void;
  group?: CriteriaGroup;
  mode: 'add' | 'edit';
  existingGroups?: CriteriaGroup[];
}

export function CriteriaGroupForm({
  open,
  onOpenChange,
  onSave,
  group,
  mode,
  existingGroups = [],
}: CriteriaGroupFormProps) {
  const { t } = useTranslation('criteria');

  const [formData, setFormData] = useState<Partial<CriteriaGroup>>(() => ({
    id: group?.id || generateId(),
    name: group?.name || '',
    weight: group?.weight ?? 0,
    criteria: group?.criteria || [],
  }));

  const [errors, setErrors] = useState<{
    name?: string;
    weight?: string;
  }>({});

  // Update form when group changes
  useEffect(() => {
    if (open) {
      setFormData({
        id: group?.id || generateId(),
        name: group?.name || '',
        weight: group?.weight ?? 0,
        criteria: group?.criteria || [],
      });
      setErrors({});
    }
  }, [open, group]);

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    if (!formData.name?.trim()) {
      newErrors.name = t('form.group.errors.nameRequired');
    } else if (formData.name.length > 255) {
      newErrors.name = t('form.group.errors.nameTooLong', { max: 255 });
    }

    if (formData.weight === undefined || formData.weight === null) {
      newErrors.weight = t('form.group.errors.weightRequired');
    } else if (formData.weight < 0) {
      newErrors.weight = t('form.group.errors.weightNegative');
    } else if (formData.weight > 100) {
      newErrors.weight = t('form.group.errors.weightTooHigh');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    onSave({
      id: formData.id!,
      name: formData.name!.trim(),
      weight: formData.weight!,
      criteria: formData.criteria!,
    });

    // Reset form
    setFormData({
      id: generateId(),
      name: '',
      weight: 0,
      criteria: [],
    });
    setErrors({});
    onOpenChange(false);
  };

  const handleCancel = () => {
    setFormData({
      id: group?.id || generateId(),
      name: group?.name || '',
      weight: group?.weight ?? 0,
      criteria: group?.criteria || [],
    });
    setErrors({});
    onOpenChange(false);
  };

  // Calculate remaining weight available
  const otherGroupsWeight = existingGroups
    .filter((g) => g.id !== group?.id)
    .reduce((sum, g) => sum + g.weight, 0);
  const remainingWeight = 100 - otherGroupsWeight;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {mode === 'add'
                ? t('form.group.titleAdd')
                : t('form.group.titleEdit')}
            </DialogTitle>
            <DialogDescription>
              {mode === 'add'
                ? t('form.group.descriptionAdd')
                : t('form.group.descriptionEdit')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="group-name">
                {t('form.group.name')} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="group-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('form.group.namePlaceholder')}
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? 'group-name-error' : undefined}
                maxLength={255}
              />
              {errors.name && (
                <p id="group-name-error" className="text-sm text-destructive" role="alert">
                  {errors.name}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                {formData.name?.length || 0}/255
              </p>
            </div>

            {/* Weight */}
            <div className="space-y-2">
              <Label htmlFor="group-weight">
                {t('form.group.weight')} <span className="text-destructive">*</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="group-weight"
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={formData.weight ?? ''}
                  onChange={(e) =>
                    setFormData({ ...formData, weight: parseInt(e.target.value) || 0 })
                  }
                  aria-invalid={!!errors.weight}
                  aria-describedby={errors.weight ? 'group-weight-error' : 'group-weight-hint'}
                  className="flex-1"
                />
                <span className="text-sm font-medium text-muted-foreground">%</span>
              </div>
              {errors.weight && (
                <p id="group-weight-error" className="text-sm text-destructive" role="alert">
                  {errors.weight}
                </p>
              )}
              <p id="group-weight-hint" className="text-xs text-muted-foreground">
                {t('form.group.weightHint', { remaining: remainingWeight })}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit">
              {mode === 'add' ? t('common:actions.add') : t('common:actions.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
